import { useAnimationStep } from "@/utils/animation/useAnimationStep";
import { useElementSize } from "@vueuse/core";
import Icon from "components/icon";
import { lerp, smoothstep } from "math";
import type { CSSProperties } from "vue";

export const CollapsedSection = defineComponent<{
  title: MaybeRef<string>;
  description?: MaybeRef<string | undefined> | MaybeRef<string> | MaybeRef<undefined>;
}>((props, ctx) => {

  const contentRef = ref();
  const contentSize = useElementSize(contentRef);
  const isOpen = ref<boolean>(false);
  const anim = useAnimationStep({ initial: 0.0, duration: 0.25 });

  const setOpen = (open: boolean) => {
    isOpen.value = open;
    anim.to(Number(open));
  }

  const title = computed(() => unref(props.title));
  const description = computed(() => unref(props.description));

  const handleClick = () => {
    setOpen(!isOpen.value);
  }

  const contentHeight = computed(() => lerp(0.0, contentSize.height.value, smoothstep(0.0, 1.0, anim.current.value)));
  const rotation = computed(() => lerp(180, 270, smoothstep(0.0, 1.0, anim.current.value)));

  const contentStyle = computed((): CSSProperties => {
    return {
      height: `${contentHeight.value}px`,
      overflow: 'hidden'
    }
  })

  return () => {
    return <div class={`w-full grid grid-auto-rows ${isOpen.value ? 'gap-6' : ''}`}>
      <div
        class="grid grid-cols-1 md:grid-cols-[max-content_max-content] justify-between items-center flex-none w-full cursor-pointer"
        onClick={handleClick}
      >
        <div class="grid grid-cols-[max-content_max-content] items-center gap-2">
          <div
            class="select-none w-[22px] h-[22px] bg-primary-100 grid items-center justify-center text-primary-500 hover:bg-primary-200 hover:text-primary-600 transition-colors cursor-pointer">
            <Icon
              src={async () => import('assets/img/svg/icons/chevron_left.svg?raw')}
              fill="currentColor"
              stroke="none"
              size="14px"
              style={{
                rotate: `${rotation.value}deg`
              }}
            />
          </div>
          <div>
            <div class="justify-start text-gray-900 text-base font-medium font-['Inter'] leading-none">{title.value}</div>
          </div>
        </div>
        <div>
          {description.value && <div class="justify-start text-gray-500 text-sm font-normal  pl-[30px] md:pl-0">{description.value}</div>}
        </div>
      </div>
      <div style={contentStyle.value}>
        <div class="w-full h-fit" ref={contentRef}>
          {ctx.slots.default ? ctx.slots.default() : ''}
        </div>
      </div>
    </div>;
  }
}, { props: ['title', 'description'] })
